import {
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import ActivationCode from "../../models/activationCode"
import Team from "../../models/team"
import { copyTextToClipboard } from "../../services/utilities/utility"
import {
  CheckRounded,
  CloseRounded,
  ContentCopyRounded,
  DeleteForeverRounded,
  EditRounded,
  InfoOutlined,
} from "@mui/icons-material"
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react"
import { ActivationCodesContext } from "../../controllers/activationCodes"
import { TeamsContext } from "../../controllers/teams"
import ActivationCodeDomainsRow from "./activationCodeDomainsRow"

const TeamActivationCodeCard = ({
  item,
  index,
  currentTeam,
  setCurrentTeam,
  setCopyFeedbackOpen,
  setCodeToDelete,
  handleDeleteCodeDialogChange,
}: {
  item: ActivationCode
  index: number
  currentTeam: Team
  setCurrentTeam: Dispatch<SetStateAction<Team>>
  setCopyFeedbackOpen: Dispatch<SetStateAction<boolean>>
  setCodeToDelete: Dispatch<SetStateAction<string>>
  handleDeleteCodeDialogChange: () => void
}) => {
  const { editMode } = useContext(TeamsContext)
  const { upsertActivationCode } = useContext(ActivationCodesContext)

  // states
  const [loading, setLoading] = useState<boolean>(false)
  const [editing, setEditing] = useState<boolean>(false)
  const [currentDomainsWhiteList, setCurrentDomainsWhiteList] = useState<
    string[] | null
  >(item.domainsWhiteList)
  const [domainToAdd, setDomainToAdd] = useState<string>("")

  useEffect(() => {
    if (!editMode) {
      setEditing(false)
    }
  }, [editMode])

  // reset data when user cancels edit
  useEffect(() => {
    if (!editing) {
      setCurrentDomainsWhiteList(item.domainsWhiteList)
      setDomainToAdd("")
    }
  }, [editing])

  return (
    <Card
      key={item.code}
      variant="outlined"
      sx={{
        boxShadow: "none",
        borderTopLeftRadius: index === 0 ? undefined : 0,
        borderTopRightRadius: index === 0 ? undefined : 0,
        borderBottomLeftRadius:
          index ===
          currentTeam.accessCodes
            .filter((item) => item.groupId === "group_default")
            .slice(0, 3).length -
            1
            ? undefined
            : 0,
        borderBottomRightRadius:
          index ===
          currentTeam.accessCodes
            .filter((item) => item.groupId === "group_default")
            .slice(0, 3).length -
            1
            ? undefined
            : 0,
        borderTop: index === 0 ? undefined : "none",
      }}
    >
      <CardContent
        style={{
          position: "relative",
          paddingTop: 10,
          paddingBottom: editing ? 16 : 10,
          paddingRight: 12,
        }}
      >
        <Stack>
          <Stack direction="row" alignItems="center">
            <Stack
              gap={2}
              direction="row"
              alignItems="center"
              style={{ flex: 1 }}
            >
              <Typography>{item.code}</Typography>
              {item.domainsWhiteList &&
              item.domainsWhiteList.length &&
              !editing ? (
                item.domainsWhiteList.length > 3 ? (
                  <Stack gap={0.5} direction="row">
                    <Typography style={{ fontSize: "0.8rem" }}>
                      {`Whitelisted for ${item.domainsWhiteList
                        .slice(0, 3)
                        .join(", ")}`}
                    </Typography>
                    <Tooltip
                      title={item.domainsWhiteList.slice(3).join(", ")}
                      arrow
                      sx={{ cursor: "pointer" }}
                    >
                      <Typography style={{ fontSize: "0.8rem" }}>
                        {`and ${item.domainsWhiteList.slice(3).length} more`}
                      </Typography>
                    </Tooltip>
                  </Stack>
                ) : (
                  <Typography style={{ fontSize: "0.8rem" }}>
                    {`Whitelisted for ${item.domainsWhiteList.join(", ")}`}
                  </Typography>
                )
              ) : null}
              {item.isPrimary && (
                <Tooltip title="Code used internally for the Team invitation functionality">
                  <Chip
                    label={
                      <Stack gap={0.5} direction="row" alignItems="center">
                        <span>Primary</span>
                        <InfoOutlined style={{ fontSize: 12 }} />
                      </Stack>
                    }
                    size="small"
                    color="success"
                  />
                </Tooltip>
              )}
            </Stack>
            <Stack gap={2} direction="row" alignItems="center">
              {/* edit */}
              {editing ? (
                <>
                  {!currentTeam.accessCodes.some(
                    (accessCode) => accessCode.isPrimary
                  ) && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={async () => {
                        setLoading(true)
                        const result = await upsertActivationCode(
                          item.code,
                          item.codeType,
                          item.policy,
                          item.maxIteration,
                          { teamId: item.teamId },
                          currentDomainsWhiteList,
                          true
                        )
                        if (result) {
                          setCurrentTeam((current) => ({
                            ...current,
                            accessCodes: current.accessCodes
                              ? [
                                  ...current.accessCodes.slice(0, index),
                                  result,
                                  ...current.accessCodes.slice(index + 1),
                                ]
                              : current.accessCodes,
                          }))
                        }
                        setLoading(false)
                        setEditing(false)
                      }}
                      disabled={loading}
                    >
                      Set as primary
                    </Button>
                  )}
                  <IconButton
                    sx={{
                      width: 40,
                      height: 40,
                    }}
                    onClick={() => setEditing(false)}
                    disabled={loading}
                  >
                    <CloseRounded
                      style={{
                        width: 24,
                      }}
                    />
                  </IconButton>
                  <IconButton
                    sx={{
                      width: 40,
                      height: 40,
                    }}
                    onClick={async () => {
                      setLoading(true)

                      const result = await upsertActivationCode(
                        item.code,
                        item.codeType,
                        item.policy,
                        item.maxIteration,
                        { teamId: item.teamId },
                        currentDomainsWhiteList,
                        item.isPrimary
                      )

                      if (result) {
                        setCurrentTeam((current) => ({
                          ...current,
                          accessCodes: current.accessCodes
                            ? [
                                ...current.accessCodes.slice(0, index),
                                result,
                                ...current.accessCodes.slice(index + 1),
                              ]
                            : current.accessCodes,
                        }))
                      }

                      setLoading(false)
                      setEditing(false)
                    }}
                    disabled={loading}
                  >
                    <CheckRounded
                      style={{
                        width: 24,
                      }}
                    />
                  </IconButton>
                </>
              ) : (
                <IconButton
                  sx={{
                    width: 40,
                    height: 40,
                  }}
                  onClick={() => setEditing(true)}
                  disabled={!editMode}
                >
                  <EditRounded
                    style={{
                      width: 20,
                    }}
                  />
                </IconButton>
              )}
              {/* copy */}
              {!editing && (
                <IconButton
                  sx={{
                    width: 40,
                    height: 40,
                  }}
                  onClick={() => {
                    copyTextToClipboard(item.code)
                    setCopyFeedbackOpen(true)
                  }}
                >
                  <ContentCopyRounded sx={{ fontSize: 20 }} />
                </IconButton>
              )}
              {/* delete */}
              <IconButton
                sx={{
                  width: 40,
                  height: 40,
                }}
                color="error"
                onClick={() => {
                  setCodeToDelete(item.code)
                  handleDeleteCodeDialogChange()
                }}
                disabled={loading || item.isPrimary}
              >
                <DeleteForeverRounded style={{ width: 24 }} />
              </IconButton>
            </Stack>
          </Stack>
          {editing && (
            <Stack>
              <Stack gap={0.5} direction="row" alignItems="center">
                <Typography style={{ fontSize: "0.9rem", fontWeight: 600 }}>
                  Domains Whitelist
                </Typography>
                {currentDomainsWhiteList && currentDomainsWhiteList.length ? (
                  <Typography style={{ fontSize: "0.8rem" }}>
                    (hover to show delete button)
                  </Typography>
                ) : null}
              </Stack>
              {currentDomainsWhiteList
                ? currentDomainsWhiteList.map((domain, index) => (
                    <ActivationCodeDomainsRow
                      key={index}
                      domain={domain}
                      index={index}
                      setCopyFeedbackOpen={setCopyFeedbackOpen}
                      currentDomainsWhiteList={currentDomainsWhiteList}
                      setCurrentDomainsWhiteList={setCurrentDomainsWhiteList}
                      disabled={loading}
                    />
                  ))
                : null}
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  setCurrentDomainsWhiteList((current) =>
                    current
                      ? [...current, domainToAdd.trim()]
                      : [domainToAdd.trim()]
                  )
                  setDomainToAdd("")
                }}
              >
                <Stack gap={1} direction="row" style={{ marginTop: 8 }}>
                  <TextField
                    size="small"
                    label="Domain"
                    value={domainToAdd}
                    onChange={(e) => setDomainToAdd(e.target.value)}
                    disabled={loading}
                  />
                  <Button
                    variant="contained"
                    disabled={
                      !/^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/.test(domainToAdd) ||
                      loading
                    }
                    type="submit"
                  >
                    Add
                  </Button>
                </Stack>
              </form>
            </Stack>
          )}
        </Stack>
        {loading && (
          <LinearProgress
            style={{ width: "100%", position: "absolute", left: 0, bottom: 0 }}
          />
        )}
      </CardContent>
    </Card>
  )
}

export default TeamActivationCodeCard
